import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Headset } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAuth } from "../../auth/AuthProvider";
import AccountMenu from "./AccountMenu";

import logo from "../../assets/main-logo-blue.svg";

type AppHeaderProps = {
  title?: string;
  backTo?: string;
  showConsultingPill?: boolean;
  rightNode?: React.ReactNode;
};

export default function AppHeader({
  title,
  backTo = "/dashboard",
  showConsultingPill = false,
  rightNode,
}: AppHeaderProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();

  const shouldShowBackButton = backTo && location.pathname !== backTo;

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
      <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="flex items-center gap-2 text-slate-900 font-bold tracking-tight"
          >
            <img src={logo} alt="Hotel Foundry" className="h-12 w-auto" />
          </Link>

          {shouldShowBackButton && (
            <Link to={backTo}>
              <Button variant="ghost" size="sm" className="text-slate-600">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            </Link>
          )}

          <h1 className="text-lg font-semibold text-slate-900 hidden md:block">
            {title}
          </h1>
        </div>

        <div className="flex items-center space-x-4">
          {showConsultingPill && (
            <Link to="/consultancy" className="hidden lg:block">
              <div className="flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors cursor-pointer">
                <Headset className="h-3 w-3" />
                <span>Consulting</span>
                <Badge
                  variant="secondary"
                  className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5"
                >
                  Beta
                </Badge>
              </div>
            </Link>
          )}

          {rightNode ? (
            rightNode
          ) : user ? (
            <AccountMenu />
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/signin">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:text-slate-900"
                >
                  Sign in
                </Button>
              </Link>
              <Link to="/membership" className="hidden sm:block">
                <Button
                  size="sm"
                  className="bg-brand-600 hover:bg-brand-700 text-white shadow-lg font-semibold"
                >
                  Get Started
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
